import { FilePdfOutlined, SendOutlined } from '@ant-design/icons';
import { Button, Empty, Mentions, Space, Spin, Typography, message as antdMessage } from 'antd';
import { useRequest } from 'ahooks';
import { useEffect, useRef, useState } from 'react';
import { CustomerPoolEnum, CustomerStateEnum, oSSController } from '../../api';
import {
  aiAssistantApi,
  aiMentionApi,
  aiMentionCustomerApi,
  aiMentionDepartmentApi,
} from '../../api/aiAssistant';
import { customerPoolMapping, customerStateMapping } from '../../constants';
import { useCompany } from '../../hooks/company';
import { canUseAiDepartment } from './scope';
import { parseChartSpecs } from './ChartRenderer';
import {
  MENTION_PREFIX,
  MENTION_PREFIXES,
  MentionPick,
  encodeMentions,
  mentionKindOfPrefix,
  pruneMentionPicks,
} from './mention';
import MessageBubble, { BubbleMessage } from './MessageBubble';
import { ToolBatch, ToolTraceItem, applySkillLoad, flattenBatches, foldMessages } from './trace';
import { useChatStream } from './useChatStream';

type ChatMessage = BubbleMessage;

/** 与后端 MyApplicationListener.AI_EXPORT_PDF_CODE 对应 */
export const AI_EXPORT_PDF_CODE = 'page:ai-assistant:export-pdf';

interface Props {
  companyId: string | number;
  conversationId: string | null;
  /** 首轮对话服务端创建会话后回调（父组件切换选中并刷新列表） */
  onConversationCreated: (id: string) => void;
  /** 一轮对话结束后刷新会话列表（更新排序） */
  onRoundFinished: () => void;
}

/** 就地更新最后一条助手消息（流式事件都作用在它身上） */
function mapLastAssistant(
  messages: ChatMessage[],
  update: (message: ChatMessage) => ChatMessage,
): ChatMessage[] {
  const next = [...messages];
  for (let i = next.length - 1; i >= 0; i--) {
    if (next[i].role === 'ASSISTANT') {
      next[i] = update(next[i]);
      break;
    }
  }
  return next;
}

/** 把新的工具调用追加进当前批次。每轮一个气泡，同一轮里并发的调用同属一批 */
function appendToolCall(batches: ToolBatch[] | undefined, item: ToolTraceItem): ToolBatch[] {
  const current = batches || [];
  if (current.length === 0) {
    return [[item]];
  }
  return [...current.slice(0, -1), [...current[current.length - 1], item]];
}

/** tool_result 回填到对应的 tool_call 上；没配上（id 缺失）就补进最后一批 */
function completeToolCall(
  batches: ToolBatch[] | undefined,
  toolCallId: string,
  name: string,
  summary: string,
): ToolBatch[] {
  const current = batches || [];
  for (let i = current.length - 1; i >= 0; i--) {
    const index = current[i].findIndex((item) => item.toolCallId === toolCallId && item.running);
    if (index !== -1) {
      const next = current.map((batch) => [...batch]);
      next[i][index] = { ...next[i][index], result: summary, running: false };
      return next;
    }
  }
  return appendToolCall(current, { toolCallId, name, result: summary });
}

const ChatWindow = (props: Props) => {
  const { companyId, conversationId, onConversationCreated, onRoundFinished } = props;
  // companyId 走 props（父组件传入），这里取判定部门功能可用性需要的用户与权限，以及气泡头像素材
  const { currentUser, hasPermission, logoOss, logoUrl } = useCompany();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loadingHistory, setLoadingHistory] = useState(false);
  const { send, abort, streaming } = useChatStream(companyId);
  // 输入框里已选中的 @ 员工，按插入顺序；发送时据此把明文 @姓名 还原成 markdown 链接
  const [mentionPicks, setMentionPicks] = useState<MentionPick[]>([]);
  // 自己刚创建的会话 id：父组件回切 conversationId 时跳过历史加载，避免打断流式中的消息
  const createdIdRef = useRef<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  // 导出 PDF 的截图范围：消息列表本身（高度是完整内容高度，不受滚动容器裁剪）
  const messagesRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState(false);
  // 导出是异步的，中途切会话时用它复核当前选中的还是不是同一个会话
  const conversationIdRef = useRef(conversationId);
  conversationIdRef.current = conversationId;
  // 是否跟随最新消息滚动。用户往上翻看历史时必须停止跟随，否则一直被拽回底部
  const stickToBottomRef = useRef(true);

  // 气泡头像整窗只取一次 URL，逐气泡取会随消息数放大请求量。
  // 取不到（没传头像 / 请求失败）时保持 undefined，气泡回退默认图标。
  // useRequest 刷新中/失败时会保留上一次的 data，而切公司时本组件会被路由复用：
  // 请求结果必须绑定发起时的上下文 key，与当前上下文不一致就弃用，避免串显旧公司素材
  const userAvatarKey = `${companyId}:${currentUser?.id}`;
  const { data: userAvatar } = useRequest(
    async () => ({
      key: userAvatarKey,
      url: await oSSController.getOSSUrl(companyId, `avatar/${currentUser.id}`),
    }),
    {
      ready: !!currentUser?.id,
      refreshDeps: [companyId, currentUser?.id],
      onError: () => undefined,
    },
  );
  const userAvatarUrl = userAvatar?.key === userAvatarKey ? userAvatar.url : undefined;
  // 公司 logo 与侧边栏同一套来源：OSS 上传过走签名 URL，否则用配置的外链
  const { data: ossLogo } = useRequest(
    async () => ({
      key: String(companyId),
      url: await oSSController.getOSSUrl(companyId, `logo/${companyId}`),
    }),
    {
      ready: !!logoOss,
      refreshDeps: [companyId, logoOss],
      onError: () => undefined,
    },
  );
  const ossLogoUrl = ossLogo?.key === String(companyId) ? ossLogo.url : undefined;
  const assistantAvatarUrl = logoOss ? ossLogoUrl : logoUrl;

  useEffect(() => {
    stickToBottomRef.current = true;
    if (conversationId === null) {
      // 新建对话/删除当前会话：同样要中止旧流，防止其继续写状态或把界面拉回旧会话
      abort();
      setMessages([]);
      return;
    }
    if (createdIdRef.current === conversationId) {
      createdIdRef.current = null;
      return;
    }
    abort();
    // 快速切换会话时旧请求可能后返回，用 stale 标志丢弃过期结果
    let stale = false;
    setLoadingHistory(true);
    aiAssistantApi
      .listMessages(companyId, conversationId)
      .then((list) => {
        if (stale) return;
        // TOOL 消息不单独成泡，折进发起它的 assistant 消息的工具轨迹
        setMessages(foldMessages(list));
      })
      .catch(() => {
        if (!stale) antdMessage.error('加载历史消息失败');
      })
      .finally(() => {
        if (!stale) setLoadingHistory(false);
      });
    return () => {
      stale = true;
    };
  }, [companyId, conversationId, abort]);

  useEffect(() => {
    if (stickToBottomRef.current) {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
    }
  }, [messages]);

  // 距底部 40px 内算「在底部」：滚动条本身有几像素误差，卡太死会误判成用户上翻
  const handleScroll = () => {
    const element = scrollRef.current;
    if (!element) return;
    const distanceToBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
    stickToBottomRef.current = distanceToBottom <= 40;
  };

  const patchLastAssistant = (patch: Partial<ChatMessage>) => {
    setMessages((prev) => mapLastAssistant(prev, (message) => ({ ...message, ...patch })));
  };

  // token 级 delta 先入缓冲，40ms 批量刷一次，避免长回复下每字符全量重渲染
  const pendingDeltaRef = useRef('');
  const pendingReasoningRef = useRef('');
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushDelta = () => {
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    const delta = pendingDeltaRef.current;
    const reasoning = pendingReasoningRef.current;
    if (!delta && !reasoning) return;
    pendingDeltaRef.current = '';
    pendingReasoningRef.current = '';
    setMessages((prev) =>
      mapLastAssistant(prev, (message) => ({
        ...message,
        content: delta ? (message.content || '') + delta : message.content,
        liveReasoning: reasoning ? (message.liveReasoning || '') + reasoning : message.liveReasoning,
      })),
    );
  };

  const scheduleFlush = () => {
    if (!flushTimerRef.current) {
      flushTimerRef.current = setTimeout(flushDelta, 40);
    }
  };

  const enqueueDelta = (delta: string) => {
    pendingDeltaRef.current += delta;
    scheduleFlush();
  };

  const enqueueReasoning = (delta: string) => {
    pendingReasoningRef.current += delta;
    scheduleFlush();
  };

  // @ 员工 / # 客户 / ~ 部门：各自一套候选，服务端按数据范围收敛（个人只能搜到自己名下的）
  const [activePrefix, setActivePrefix] = useState(MENTION_PREFIX.user);
  // 业务员（仅本人范围或没有部门工具权限）连 ~ 触发符都不给，避免给出用不了的入口
  const departmentEnabled = canUseAiDepartment({
    isSuperAdmin: currentUser?.isSuperAdmin,
    departmentId: currentUser?.departmentId,
    hasPermission,
  });
  const mentionPrefixes = departmentEnabled
    ? MENTION_PREFIXES
    : MENTION_PREFIXES.filter((prefix) => prefix !== MENTION_PREFIX.department);
  const { data: mentionUsers, run: searchUsers, loading: userLoading } = useRequest(
    async (keyword: string) => aiMentionApi.search(companyId, keyword),
    { manual: true, debounceWait: 300 },
  );
  const { data: mentionCustomers, run: searchCustomers, loading: customerLoading } = useRequest(
    async (keyword: string) => aiMentionCustomerApi.search(companyId, keyword),
    { manual: true, debounceWait: 300 },
  );
  // ~ 部门：只对管理层开放（有部门工具权限码且数据范围不是仅本人），业务员连触发符都不给
  const { data: mentionDepartments, run: searchDepartments, loading: departmentLoading } = useRequest(
    async (keyword: string) => aiMentionDepartmentApi.search(companyId, keyword),
    { manual: true, debounceWait: 300 },
  );

  // Mentions 是 textarea，塞不下富文本：候选只插入姓名，id 记在 mentionPicks 里
  const userOptions = (mentionUsers || []).map((u) => ({
    key: String(u.userId),
    value: u.name,
    mentionId: String(u.userId),
    label: (
      <span>
        {u.name}
        <Typography.Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
          {[u.departmentName, u.mobileMasked].filter(Boolean).join(' · ')}
        </Typography.Text>
      </span>
    ),
  }));

  // 客户重名率高，副标题要给足区分信息：手机号 + 星级 + 状态 + 归属
  const customerOptions = (mentionCustomers || []).map((c) => ({
    key: String(c.customerId),
    value: c.name,
    mentionId: String(c.customerId),
    label: (
      <span>
        {c.name}
        <Typography.Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
          {[
            c.mobileMasked,
            c.star == null ? null : `${c.star}星`,
            customerStateMapping[c.state as CustomerStateEnum],
            c.ownerUserName || customerPoolMapping[c.pool as CustomerPoolEnum],
          ]
            .filter(Boolean)
            .join(' · ')}
        </Typography.Text>
      </span>
    ),
  }));

  // 部门重名少，副标题给上级部门和人数即可
  const departmentOptions = (mentionDepartments || []).map((d) => ({
    key: String(d.departmentId),
    value: d.name,
    mentionId: String(d.departmentId),
    label: (
      <span>
        {d.name}
        <Typography.Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
          {[d.parentName, d.memberCount == null ? null : `${d.memberCount} 人`]
            .filter(Boolean)
            .join(' · ')}
        </Typography.Text>
      </span>
    ),
  }));

  const isCustomerPrefix = activePrefix === MENTION_PREFIX.customer;
  const isDepartmentPrefix = activePrefix === MENTION_PREFIX.department;
  const mentionOptions = isCustomerPrefix
    ? customerOptions
    : isDepartmentPrefix
      ? departmentOptions
      : userOptions;
  const mentionLoading = isCustomerPrefix
    ? customerLoading
    : isDepartmentPrefix
      ? departmentLoading
      : userLoading;
  const notFoundContent = isCustomerPrefix
    ? '没有匹配的客户'
    : isDepartmentPrefix
      ? '没有匹配的部门'
      : '没有匹配的员工';

  // 空会话引导：已开启技能自带的示例提问，后端没有该端点时静默降级为不展示
  const { data: starters } = useRequest(() => aiAssistantApi.listStarters(companyId), {
    refreshDeps: [companyId],
    onError: () => undefined,
  });
  // 旧后端没有该端点时会返回 HTML 之类的非数组内容，直接 map 会把整个聊天页炸掉
  const starterList = Array.isArray(starters) ? starters : [];

  /** @param preset 点击示例提问时直接发送的文本，缺省则取输入框内容 */
  const doSend = async (preset?: string) => {
    const raw = (preset ?? input).trim();
    // 导出中不允许发新消息：截图取的是 messagesRef 当前的 DOM，
    // 中途追加消息会让「量高度」和「截图」看到两份不同的正文（末尾被裁 / 截进流式半成品）
    if (!raw || streaming || exporting) return;
    // 明文 @姓名 → @[姓名](user://userId)，后端与 LLM 只认后者
    // 示例提问是纯文本，没有 @ / # 选择记录，按原样发送
    const content = preset ? raw : encodeMentions(raw, mentionPicks);
    setInput('');
    setMentionPicks([]);
    // 自己发了新消息，重新跟到底部
    stickToBottomRef.current = true;
    setMessages((prev) => [
      ...prev,
      { role: 'USER', content, status: 'COMPLETED' },
      { role: 'ASSISTANT', content: '', streaming: true, status: 'GENERATING' },
    ]);
    try {
      await send(
        { conversationId: conversationId ?? undefined, content },
        {
          onConversation: (id) => {
            createdIdRef.current = id;
            onConversationCreated(id);
          },
          // agent 多轮：每轮一个 message_start，对应落库的一条 assistant 消息。
          // 上一轮气泡有过实质内容（正文/思考/工具轨迹）就按历史回放的样子原地封口、
          // 另起新气泡，回答过程中的展示与回放历史保持一致。
          // 图表例外：历史里 charts 落在最后一条消息上，这里同样随最新气泡走
          onMessageStart: () => {
            flushDelta();
            setMessages((prev) => {
              const next = [...prev];
              const last = next[next.length - 1];
              const hasSubstance = Boolean(
                last?.content || last?.liveReasoning || flattenBatches(last?.toolBatches).length > 0,
              );
              if (last?.role === 'ASSISTANT' && last.streaming && hasSubstance) {
                next[next.length - 1] = {
                  ...last,
                  streaming: false,
                  status: 'COMPLETED',
                  liveCharts: [],
                };
                next.push({
                  role: 'ASSISTANT',
                  content: '',
                  streaming: true,
                  status: 'GENERATING',
                  liveCharts: last.liveCharts,
                });
              }
              return next;
            });
          },
          onDelta: enqueueDelta,
          onReasoning: enqueueReasoning,
          // 技能装配过程：搜索结果先落 skillTrace，加载清单到达后回填 skills
          onSkillSearch: (total, matched, names) => {
            flushDelta();
            setMessages((prev) =>
              mapLastAssistant(prev, (message) => ({
                ...message,
                skillTrace: { total, matched, names },
              })),
            );
          },
          onSkillLoad: (skills) =>
            setMessages((prev) =>
              mapLastAssistant(prev, (message) => ({
                ...message,
                skillTrace: applySkillLoad(message.skillTrace, skills),
              })),
            ),
          onToolCall: (toolCallId, name, args) => {
            flushDelta();
            setMessages((prev) =>
              mapLastAssistant(prev, (message) => ({
                ...message,
                toolBatches: appendToolCall(message.toolBatches, {
                  toolCallId,
                  name,
                  arguments: args,
                  running: true,
                }),
              })),
            );
          },
          onToolResult: (toolCallId, name, summary) =>
            setMessages((prev) =>
              mapLastAssistant(prev, (message) => ({
                ...message,
                toolBatches: completeToolCall(message.toolBatches, toolCallId, name, summary),
              })),
            ),
          onChart: (chartJson) =>
            setMessages((prev) =>
              mapLastAssistant(prev, (message) => ({
                ...message,
                liveCharts: [...(message.liveCharts || []), ...parseChartSpecs(chartJson)],
              })),
            ),
          onDone: () => {
            flushDelta();
            patchLastAssistant({ streaming: false, status: 'COMPLETED' });
          },
          onError: (msg) => {
            flushDelta();
            antdMessage.error(msg);
            patchLastAssistant({ streaming: false, status: 'FAILED', errorMessage: msg });
          },
        },
      );
      // 流正常 EOF 但服务端没发 done/error（连接被中间层掐断等）：不能让气泡永远停在流式态
      flushDelta();
      setMessages((prev) =>
        mapLastAssistant(prev, (message) =>
          message.streaming ? { ...message, streaming: false, status: 'INTERRUPTED' } : message,
        ),
      );
    } catch (e: any) {
      flushDelta();
      if (e?.name === 'AbortError') return;
      const msg = e?.message || 'AI 回复失败，请稍后重试';
      antdMessage.error(msg);
      patchLastAssistant({ streaming: false, status: 'FAILED', errorMessage: msg });
    } finally {
      onRoundFinished();
    }
  };

  /**
   * 导出当前会话为 PDF。水印与文件名由服务端签发——无导出权限时这一步就 403，
   * 因此按钮隐藏只是体验，真正的闸门在服务端。
   *
   * <p>整个流程有几个 await，期间用户可能切走会话（messagesRef 里换成了另一个会话的正文）。
   * 每个异步边界都要复核会话没变，否则会拿 A 会话的水印/文件名/审计记录导出 B 会话的内容。
   */
  const handleExport = async () => {
    const targetId = conversationId;
    if (!targetId || !messagesRef.current) return;
    // 会话在导出过程中被切走就放弃，不给用户一份张冠李戴的 PDF
    const switchedAway = () => conversationIdRef.current !== targetId;
    setExporting(true);
    try {
      const ticket = await aiAssistantApi.createExportTicket(companyId, targetId);
      if (switchedAway()) return;
      // jspdf/html2canvas 体积较大，用到时才加载
      const { exportConversationToPdf } = await import('./exportPdf');
      if (switchedAway() || !messagesRef.current) return;
      await exportConversationToPdf(messagesRef.current, ticket, switchedAway);
      if (switchedAway()) return;
      antdMessage.success('已导出 PDF');
    } catch (e: any) {
      if (switchedAway()) return;
      antdMessage.error(e?.message || '导出 PDF 失败');
    } finally {
      setExporting(false);
    }
  };

  const canExport = hasPermission(AI_EXPORT_PDF_CODE);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      {canExport && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', paddingBottom: 8 }}>
          <Button
            icon={<FilePdfOutlined />}
            data-testid="export-pdf"
            loading={exporting}
            // 会话没落库（首轮流式中）、历史还在加载或没有消息时无从导出
            disabled={!conversationId || streaming || loadingHistory || messages.length === 0}
            onClick={handleExport}
          >
            导出 PDF
          </Button>
        </div>
      )}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        data-testid="chat-scroll"
        style={{ flex: 1, overflowY: 'auto', padding: '0 8px' }}
      >
        <Spin spinning={loadingHistory}>
          {messages.length === 0 && !loadingHistory ? (
            <div style={{ marginTop: 100 }} data-testid="chat-starters">
              <Empty description="向 AI 助手提问，分析业务数据" />
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Space direction="vertical" size={8} style={{ maxWidth: 520, width: '100%' }}>
                  {starterList.map((starter) => (
                    <Button
                      key={starter.question}
                      block
                      style={{ textAlign: 'left', whiteSpace: 'normal', height: 'auto' }}
                      title={starter.skillName ? `技能：${starter.skillName}` : undefined}
                      disabled={streaming || exporting}
                      onClick={() => doSend(starter.question)}
                    >
                      {starter.question}
                    </Button>
                  ))}
                </Space>
              </div>
            </div>
          ) : (
            <div ref={messagesRef}>
              {messages.map((m, index) => (
                <MessageBubble
                  key={m.id ?? `local-${index}`}
                  message={m}
                  userAvatarUrl={userAvatarUrl}
                  assistantAvatarUrl={assistantAvatarUrl}
                />
              ))}
            </div>
          )}
        </Spin>
      </div>
      <div style={{ display: 'flex', gap: 8, paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
        <Mentions
          style={{ flex: 1 }}
          value={input}
          onChange={(value) => {
            setInput(value);
            // 用户手动删掉某个 @ 后，对应记录也要丢弃，否则后面同名文本会被错配
            setMentionPicks((picks) => pruneMentionPicks(value, picks));
          }}
          onSelect={(option, prefix) => {
            const kind = mentionKindOfPrefix(prefix);
            if (!kind) return;
            setMentionPicks((picks) => [
              ...picks,
              { kind, name: String(option.value), id: String((option as any).mentionId) },
            ]);
          }}
          placeholder={
            departmentEnabled
              ? '输入问题，@ 员工，# 客户，~ 部门，Enter 发送，Shift + Enter 换行'
              : '输入问题，@ 员工，# 客户，Enter 发送，Shift + Enter 换行'
          }
          autoSize={{ minRows: 1, maxRows: 4 }}
          maxLength={4000}
          prefix={mentionPrefixes}
          filterOption={false}
          onSearch={(text, prefix) => {
            // 记住当前触发符：候选列表和 loading 都要跟着切
            setActivePrefix(prefix);
            if (prefix === MENTION_PREFIX.customer) {
              searchCustomers(text);
            } else if (prefix === MENTION_PREFIX.department) {
              searchDepartments(text);
            } else {
              searchUsers(text);
            }
          }}
          loading={mentionLoading}
          options={mentionOptions}
          notFoundContent={notFoundContent}
          onPressEnter={(e) => {
            if (e.shiftKey) return;
            e.preventDefault();
            doSend();
          }}
        />
        {streaming ? (
          <Button
            danger
            onClick={() => {
              abort();
              patchLastAssistant({ streaming: false, status: 'INTERRUPTED' });
            }}
          >
            停止
          </Button>
        ) : (
          <Button
            type="primary"
            icon={<SendOutlined />}
            disabled={!input.trim() || exporting}
            onClick={() => doSend()}
          >
            发送
          </Button>
        )}
      </div>
    </div>
  );
};

export default ChatWindow;
